import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  DollarSign,
  Loader2,
  Search,
  Users,
  X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { libelle, referenceLisible, nomLisible, texteSur } from '@/lib/libelles';
import { formatLitres } from '@/lib/formats';
import { globalSearch } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import BrandLogo from '@/components/BrandLogo';

/**
 * La recherche de l'application, et non celle d'un écran.
 *
 * Elle vivait dans la page d'accueil. Les maquettes la placent dans la barre
 * supérieure, et elles ont raison: chercher un matricule n'a rien à voir avec
 * l'écran qu'on regarde. Laissée dans les pages, elle s'est mise à se dupliquer
 * — cinq écrans avaient fini par en poser une chacun, dont trois qui ne
 * faisaient que rediriger vers la page de recherche.
 *
 * Elle reste distincte des champs qui **filtrent une liste**: ceux-là
 * appartiennent bien à leur écran, et leur libellé le dit («Rechercher un
 * client, une raison sociale…»). Celle-ci traverse le produit.
 */

/**
 * Le menu de résultats de la recherche rapide.
 *
 * Il était écrit deux fois — une version mobile, une version bureau — pour un
 * balisage identique à la largeur près. Deux cents lignes en double, donc deux
 * endroits à corriger et un seul qu'on pense à mettre à jour. La maquette ne
 * demande qu'un seul champ de recherche; il n'y a plus de raison d'en garder
 * deux.
 */
export const ResultatsRecherche = ({ resultats, enCours, onNaviguer }) => {
  if (enCours) {
    return (
      <div className="absolute left-0 right-0 top-full z-20 mt-2 rounded-xl border border-border bg-card p-4 text-center shadow-lg">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-flame" />
      </div>
    );
  }

  if (!resultats) return null;

  const groupes = [
    {
      cle: 'trucks',
      titre: 'Camions',
      // La marque, et non un camion générique: c'est ce que la ligne portait
      // avant l'extraction, et le résultat le plus fréquent mérite le repère
      // le plus reconnaissable.
      icone: () => <BrandLogo variant="plain" size={16} />,
      lignes: resultats.trucks || [],
      // La fiche du camion. Sans identifiant (résultat ancien ou hors ligne),
      // on retombe sur le parc filtré par plaque.
      vers: (camion) =>
        camion.id
          ? `/trucks/${camion.id}`
          : `/trucks?matricule=${encodeURIComponent(camion.matricule)}`,
      principal: (camion) => camion.matricule,
      secondaire: (camion) => formatLitres(camion.capacity_liters || 0),
      pastille: (camion) => (
        <Badge
          className={cn(
            'status-badge text-xs',
            camion.current_status === 'FREE'
              ? 'bg-success/15 text-success'
              : 'bg-flame/15 text-flame-ink',
          )}
        >
          {libelle('truckStatus', camion.current_status)}
        </Badge>
      ),
      mono: true,
    },
    {
      cle: 'operations',
      titre: 'Convois',
      icone: Briefcase,
      lignes: resultats.operations || [],
      vers: (convoi) => (convoi.id ? `/operations/${convoi.id}` : '/operations'),
      principal: (convoi) => referenceLisible(convoi.code),
      secondaire: (convoi) => texteSur(convoi.beneficiary_name, ''),
      mono: true,
    },
    {
      cle: 'clients',
      titre: 'Clients',
      icone: Users,
      lignes: resultats.clients || [],
      vers: () => '/clients',
      principal: (client) => nomLisible(client.name),
      secondaire: (client) => client.phone || 'Sans téléphone',
    },
    {
      cle: 'orders',
      titre: 'Commandes',
      icone: DollarSign,
      lignes: resultats.orders || [],
      vers: () => '/orders',
      principal: (commande) => referenceLisible(commande.reference_number),
      secondaire: (commande) => libelle('statut_commande', commande.status, 'En attente'),
      mono: true,
    },
  ].filter((groupe) => groupe.lignes.length > 0);

  return (
    <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-80 overflow-y-auto rounded-xl border border-border bg-card shadow-lg">
      {groupes.length === 0 ? (
        <div className="p-4 text-center text-sm text-muted-foreground">
          Aucun résultat
        </div>
      ) : (
        groupes.map((groupe) => {
          const Icone = groupe.icone;
          return (
            <div
              key={groupe.cle}
              className="p-2 [&+&]:border-t [&+&]:border-border"
            >
              <p className="readout-label mb-2 px-2">{groupe.titre}</p>
              {groupe.lignes.map((ligne) => (
                <button
                  key={ligne.id}
                  type="button"
                  onClick={() => onNaviguer(groupe.vers(ligne))}
                  className="flex w-full items-center gap-3 rounded p-2 text-left hover:bg-muted/50"
                >
                  <Icone className="h-4 w-4 shrink-0 text-muted-foreground/70" />
                  <span
                    className={cn(
                      'truncate font-semibold',
                      groupe.mono && 'font-mono',
                    )}
                  >
                    {groupe.principal(ligne)}
                  </span>
                  <span className="ml-auto shrink-0 text-sm text-muted-foreground">
                    {groupe.secondaire(ligne)}
                  </span>
                  {groupe.pastille ? groupe.pastille(ligne) : null}
                </button>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
};


const DELAI_FRAPPE = 300;
const LONGUEUR_MINIMALE = 2;

export const GlobalSearch = ({ className }) => {
  const navigate = useNavigate();
  const [requete, setRequete] = useState('');
  const [resultats, setResultats] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const conteneur = useRef(null);

  const effacer = useCallback(() => {
    setRequete('');
    setResultats(null);
  }, []);

  // La frappe reste immédiate à l'écran; seule la requête attend. Sans ce
  // délai, taper un matricule de huit caractères lançait huit appels.
  useEffect(() => {
    if (requete.trim().length < LONGUEUR_MINIMALE) {
      setResultats(null);
      return undefined;
    }
    const minuteur = setTimeout(async () => {
      setEnCours(true);
      try {
        setResultats(await globalSearch(requete));
      } catch (erreur) {
        // Une recherche qui échoue ne doit pas faire de bruit: l'utilisateur
        // voit un menu vide et retape, ce qui est le comportement attendu.
        setResultats(null);
      } finally {
        setEnCours(false);
      }
    }, DELAI_FRAPPE);
    return () => clearTimeout(minuteur);
  }, [requete]);

  // Un clic ailleurs referme le menu. Sans cela il reste ouvert derrière le
  // contenu de la page, et masque ce sur quoi on vient de cliquer.
  useEffect(() => {
    const auClic = (evenement) => {
      if (conteneur.current && !conteneur.current.contains(evenement.target)) {
        setResultats(null);
      }
    };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, []);

  return (
    <div ref={conteneur} className={cn('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
      <Input
        type="search"
        placeholder="Rechercher partout (matricule, convoi, client…)"
        value={requete}
        onChange={(evenement) => setRequete(evenement.target.value.toUpperCase())}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Escape') effacer();
        }}
        className="h-10 rounded-xl pl-10 pr-10 text-sm"
        aria-label="Rechercher dans toute l'application"
        data-testid="global-search-input"
      />
      {requete ? (
        <button
          type="button"
          onClick={effacer}
          aria-label="Effacer la recherche"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-muted"
        >
          <X className="h-4 w-4 text-muted-foreground/70" />
        </button>
      ) : null}
      <ResultatsRecherche
        resultats={resultats}
        enCours={enCours}
        onNaviguer={(chemin) => {
          navigate(chemin);
          effacer();
        }}
      />
    </div>
  );
};

export default GlobalSearch;
